import type File from '../interfaces/File';
import type FilesDragged from '../interfaces/FilesDragged';

/**
 * MIME type identifier used for internal drag-and-drop file transfers.
 * Ensures that only our application's file objects are recognized during DnD.
 */
export const INTERNAL_DND_MIME = 'application/x-filegrant-files';

/**
 * Constructs a FilesDragged payload for drag-and-drop operations.
 *
 * @param allSelectedKeys - Array of selected IDs (may be numbers or strings, but only numbers are considered).
 * @param anchorId - The "anchor" (the file over which the drag starts).
 * @returns An object representing the "dragged files" payload.
 */
export function buildFilesDragged(files: File[], anchorId: number): FilesDragged {
  // If the anchorId is in the selection, use the selection; otherwise, just the anchor.
  return { source: 'internal', files: files, anchorId };
}

/**
 * Checks if a DragEvent has FilesDragged content (i.e., from an internal DnD interaction).
 *
 * @param e - The DragEvent to check.
 * @returns True if the custom MIME type for file dragging is present; false otherwise.
 */
export function hasFilesDragged(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes(INTERNAL_DND_MIME);
}

/**
 * Reads and parses a FilesDragged payload from a DragEvent if present.
 *
 * @param e - The DragEvent from which to extract the dragged files payload.
 * @returns The FilesDragged payload if present and valid, or null otherwise.
 */
export function readFilesDragged(e: DragEvent): FilesDragged | null {
  try {
    // Attempt to get the custom drag data from the DataTransfer object.
    const raw = e.dataTransfer?.getData(INTERNAL_DND_MIME);
    if (!raw) return null;
    // Parse the JSON string and type-assert as FilesDragged.
    return JSON.parse(raw) as FilesDragged;
  } catch {
    // If parsing fails or MIME not present, return null gracefully
    return null;
  }
}
